import { PayloadAction, createSelector, createSlice } from '@reduxjs/toolkit';
import { RootState } from 'store/store';

import { RouteGroup } from 'app/types/router';
import { PreferredLanguage } from 'app/types/language';

import { User } from 'app/services/api/models/User';
import { Product } from 'app/services/api/models/Product';
import { Account } from 'app/services/api/models/Account';
import { UserPreference } from 'app/services/api/models/UserPreference';
import { AccountProperty } from 'app/services/api/models/AccountProperty';
import { UserRole } from 'app/services/api/models/UserRole';

import { dashboardState } from './dashboardState';
import { getBootstrapAccountTheme } from 'app/utils/account-theme';

export const dashboardSlice = createSlice({
  name: 'app',
  initialState: dashboardState,
  // The `reducers` field lets us define reducers and generate associated actions
  reducers: {
    setMyUser(state, action: PayloadAction<User | null>) {
      state.user = action.payload;
    },
    setMyAccount(state, action: PayloadAction<Account | null>) {
      state.account = action.payload;
    },

    setMyAccountTree(state, action: PayloadAction<Array<Account>>) {
      state.accountTree = action.payload;
    },
    setMyUserPreferences(state, action: PayloadAction<Array<UserPreference>>) {
      state.userPreferences = action.payload;
    },
    setMyAccountProperties(
      state,
      action: PayloadAction<Array<AccountProperty>>,
    ) {
      state.accountProperties = action.payload;
    },
    setProducts(state, action: PayloadAction<Array<Product>>) {
      state.products = action.payload;
    },
    setLogoUrl(state, action: PayloadAction<string>) {
      state.logoURL = action.payload;
    },
    setThemeColor(state, action: PayloadAction<string>) {
      state.themeColor = action.payload;
    },
    setSidebarMenus(state, action: PayloadAction<Array<RouteGroup>>) {
      state.sidebarMenus = action.payload;
    },
    setPreferredLanguage(state, action: PayloadAction<PreferredLanguage>) {
      state.language = action.payload;
    },
    setIsAppRequestPendingTo(state, action: PayloadAction<boolean>) {
      state.isAppRequestPending = action.payload;
    },
    setIsSignOutRequestPendingTo(state, action: PayloadAction<boolean>) {
      state.isSignOutRequestPending = action.payload;
    },
    setMyUserRoles(state, action: PayloadAction<Array<UserRole>>) {
      state.userRoles = action.payload;
    },
    setSupportModal(state, action: PayloadAction<boolean>) {
      state.supportModal = action.payload;
    },
    setRefreshRateInterval(state, action: PayloadAction<string>) {
      state.refreshRateInterval = action.payload;
    },
    setDataReset(state) {
      state.user = null;
      state.account = null;
      state.logoURL = '';
      state.language = 'en';
      state.themeColor = getBootstrapAccountTheme();
      state.sidebarMenus = [];
      state.userPreferences = [];
      state.accountProperties = [];
      state.userRoles = [];
      state.isAppRequestPending = false;
      state.isSignOutRequestPending = false;
    },
  },
});

/* ------- ACTION CREATORS ------- */
export const { actions: dashboardActions } = dashboardSlice;

/* ------- STATE SELECTORS ------- */
export const selectApp = (state: RootState) => state.app || dashboardState;

export const makeSelectUser = () => createSelector(selectApp, app => app.user);

// export const makeSelectAccount = () =>
//   createSelector(selectApp, app => app.account);

// export const makeSelectPreferredLanguage = () =>
//   createSelector(selectApp, app => app.language);

// export const makeSelectLogoUrl = () =>
//   createSelector(selectApp, app => app.logoURL);

// export const makeSelectThemeColor = () =>
//   createSelector(selectApp, app => app.themeColor);

// export const makeSelectSidebarMenus = () =>
//   createSelector(selectApp, app => app.sidebarMenus);

// export const makeSelectSidebarSubMenus = () =>
//   createSelector(selectApp, app => {
//     let subMenus: Array<RouteItem> = [];
//     app.sidebarMenus.forEach(sidebarMenu => {
//       subMenus = [...subMenus, ...sidebarMenu.children];
//     });

//     return subMenus;
//   });

// export const makeSelectUserPreferences = () =>
//   createSelector(selectApp, app => app.userPreferences);

// export const makeSelectAccountProperties = () =>
//   createSelector(selectApp, app => app.accountProperties);

// export const makeSelectIsAppRequestPending = () =>
//   createSelector(selectApp, app => app.isAppRequestPending);

// export const makeSelectIsSignOutRequestPending = () =>
//   createSelector(selectApp, app => app.isSignOutRequestPending);

export default dashboardSlice.reducer;
